import { Routes, UrlMatcher, UrlSegment } from '@angular/router';
import { HomePageComponent } from './core/components/homepage/homepage.component';
import { DashboardComponent } from './core/components/dashboard/dashboard.component';
import { CategoryListComponent } from './category/components/categoryList/categoryList.component';
import { CategoryDetailsComponent } from './category/components/categoryDetails/categoryDetails.component';
import { CourseListComponent } from './course/components/courseList/courseList.component';
import { CourseDetailsComponent } from './course/components/courseDetails/courseDetails.component';
import { RevenueListComponent } from './financials/components/revenue-list/revenue-List.component';
import { CreateStripeAccountComponent } from './financials/components/stripe-account/stripe-account.component';
import { UserLogoutComponent } from './user/components/logout/logout.component';
import { LoginSsoComponent } from './user/components/login-sso/login-sso.component';
import { SettingsComponent } from './core/components/settings/settings.component';
import { CommunityListComponent } from './community/components/communityList/communityList.component';
import { CommunityDetailsComponent } from './community/components/communityDetails/communityDetails.component';
import { CommunitySubscribersComponent } from './community/components/communitySubscribers/communitySubscribers.component';
import { CommunityPostListComponent } from './post/components/communityPostList/communityPostList.component';
import { CommunityPostDetailsComponent } from './post/components/communityPostDetails/communityPostDetails.component';
import { DigitalProductListComponent } from './digitalProduct/components/digitalProductList/digitalProductList.component';
import { DigitalProductDetailsComponent } from './digitalProduct/components/digitalProductDetails/digitalProductDetails.component';
import { DigitalProductPublicComponent } from './digitalProduct/components/digitalProductPublic/digitalProductPublic.component';
import { SubscriptionListComponent } from './subscription/components/subscriptionList/subscriptionList.component';
import { SubscriptionDetailsComponent } from './subscription/components/subscriptionDetails/subscriptionDetails.component';
import { SubscriptionPublicComponent } from './subscription/components/subscriptionPublic/subscriptionPublic.component';
import { SubscriptionSubscribersComponent } from './subscription/components/subscriptionSubscribers/subscriptionSubscribers.component';

const publicDigitalProductMatcher: UrlMatcher = (segments) => {
  if (segments.length !== 2) {
    return null;
  }

  const [producerSegment, slugSegment] = segments;
  let producerPath = producerSegment.path;
  let slugPath = slugSegment.path;

  try {
    producerPath = decodeURIComponent(producerPath);
    slugPath = decodeURIComponent(slugPath);
  } catch {
    producerPath = producerSegment.path;
    slugPath = slugSegment.path;
  }

  if (!producerPath.startsWith('@') || !slugPath) {
    return null;
  }

  const producerId = producerPath.slice(1).trim();
  const slug = slugPath.trim();
  if (!producerId || !slug) {
    return null;
  }

  return {
    consumed: segments,
    posParams: {
      userId: new UrlSegment(producerId, producerSegment.parameters),
      slug: new UrlSegment(slug, slugSegment.parameters)
    }
  };
};

const publicSubscriptionMatcher: UrlMatcher = (segments) => {
  if (segments.length !== 3) {
    return null;
  }

  const [producerSegment, kindSegment, slugSegment] = segments;
  let producerPath = producerSegment.path;
  let kindPath = kindSegment.path;
  let slugPath = slugSegment.path;

  try {
    producerPath = decodeURIComponent(producerPath);
    kindPath = decodeURIComponent(kindPath);
    slugPath = decodeURIComponent(slugPath);
  } catch {
    producerPath = producerSegment.path;
    kindPath = kindSegment.path;
    slugPath = slugSegment.path;
  }

  if (!producerPath.startsWith('@')) {
    return null;
  }

  const normalizedKind = kindPath.trim().toLowerCase();
  if (normalizedKind !== 'subscription' && normalizedKind !== 'subscriptions') {
    return null;
  }

  const producerId = producerPath.slice(1).trim();
  const slug = slugPath.trim();
  if (!producerId || !slug) {
    return null;
  }

  return {
    consumed: segments,
    posParams: {
      userId: new UrlSegment(producerId, producerSegment.parameters),
      slug: new UrlSegment(slug, slugSegment.parameters)
    }
  };
};

export const routes: Routes = [

  /* -------------------- PUBLIC DIGITAL PRODUCT -------------------- */
  {
    matcher: publicSubscriptionMatcher,
    component: SubscriptionPublicComponent
  },
  {
    matcher: publicDigitalProductMatcher,
    component: DigitalProductPublicComponent
  },

  /* -------------------- AUTH -------------------- */
  {
    path: 'user/login',
    loadComponent: () => import('./user/components/login/login.component'),
    data: { breadcrumb: 'breadcrumb.login' }
  },
  {
    path: 'user/logout',
    component: UserLogoutComponent
  },
  {
    path: 'login-sso',
    component: LoginSsoComponent,
    data: { breadcrumb: 'breadcrumb.loginSso' }
  },

  /* -------------------- DASHBOARD -------------------- */
  {
    path: 'dashboard',
    component: DashboardComponent,
    data: { breadcrumb: 'breadcrumb.dashboard' }
  },

  /* -------------------- CATEGORIES -------------------- */
  {
    path: 'listCategories',
    component: CategoryListComponent,
    data: { breadcrumb: 'breadcrumb.categories' }
  },
  {
    path: 'createCategory',
    component: CategoryDetailsComponent,
    data: { breadcrumb: 'breadcrumb.createCategory' }
  },
  {
    path: 'updateCategory/:id',
    component: CategoryDetailsComponent,
    data: { breadcrumb: 'breadcrumb.updateCategory' }
  },

  /* -------------------- COURSES -------------------- */
  {
    path: 'listCourses',
    component: CourseListComponent,
    data: { breadcrumb: 'breadcrumb.courses' }
  },
  {
    path: 'createCourse',
    component: CourseDetailsComponent,
    data: { breadcrumb: 'breadcrumb.createCourse' }
  },
  {
    path: 'updateCourse/:id',
    component: CourseDetailsComponent,
    data: { breadcrumb: 'breadcrumb.updateCourse' }
  },

  /* -------------------- DIGITAL PRODUCTS -------------------- */
  {
    path: 'listDigitalProducts',
    component: DigitalProductListComponent,
    data: { breadcrumb: 'breadcrumb.digitalProducts' }
  },
  {
    path: 'createDigitalProduct',
    component: DigitalProductDetailsComponent,
    data: { breadcrumb: 'breadcrumb.createDigitalProduct' }
  },
  {
    path: 'updateDigitalProduct/:id',
    component: DigitalProductDetailsComponent,
    data: { breadcrumb: 'breadcrumb.updateDigitalProduct' }
  },

  /* -------------------- SUBSCRIPTIONS -------------------- */
  {
    path: 'listSubscriptions',
    component: SubscriptionListComponent,
    data: { breadcrumb: 'breadcrumb.subscriptions' }
  },
  {
    path: 'listSubscriptionSubscribers',
    component: SubscriptionSubscribersComponent,
    data: { breadcrumb: 'breadcrumb.subscriptionSubscribers' }
  },
  {
    path: 'createSubscription',
    component: SubscriptionDetailsComponent,
    data: { breadcrumb: 'breadcrumb.createSubscription' }
  },
  {
    path: 'updateSubscription/:id',
    component: SubscriptionDetailsComponent,
    data: { breadcrumb: 'breadcrumb.updateSubscription' }
  },

  /* -------------------- COMMUNITIES -------------------- */
  {
    path: 'listCommunities',
    component: CommunityListComponent,
    data: { breadcrumb: 'breadcrumb.communities' }
  },
  {
    path: 'communitySubscribers/:id',
    component: CommunitySubscribersComponent,
    data: { breadcrumb: 'breadcrumb.communitySubscribers' }
  },
  {
    path: 'communityPosts',
    component: CommunityPostListComponent,
    data: { breadcrumb: 'breadcrumb.communityPosts' }
  },
  {
    path: 'createCommunityPost',
    component: CommunityPostDetailsComponent,
    data: { breadcrumb: 'breadcrumb.createCommunityPost' }
  },
  {
    path: 'createCommunityPost/:communityId',
    component: CommunityPostDetailsComponent,
    data: { breadcrumb: 'breadcrumb.createCommunityPost' }
  },
  {
    path: 'updateCommunityPost/:id',
    component: CommunityPostDetailsComponent,
    data: { breadcrumb: 'breadcrumb.updateCommunityPost' }
  },
  {
    path: 'updateCommunityPost/:communityId/:id',
    component: CommunityPostDetailsComponent,
    data: { breadcrumb: 'breadcrumb.updateCommunityPost' }
  },
  {
    path: 'createCommunity',
    component: CommunityDetailsComponent,
    data: { breadcrumb: 'breadcrumb.createCommunity' }
  },
  {
    path: 'updateCommunity/:id',
    component: CommunityDetailsComponent,
    data: { breadcrumb: 'breadcrumb.updateCommunity' }
  },

  /* -------------------- FINANCIALS -------------------- */
  {
    path: 'financials',
    component: RevenueListComponent,
    data: { breadcrumb: 'breadcrumb.financials' }
  },
  {
    path: 'stripeAccount',
    component: CreateStripeAccountComponent,
    data: { breadcrumb: 'breadcrumb.stripeAccount' }
  },
  {
    path: 'settings',
    component: SettingsComponent,
    data: { breadcrumb: 'breadcrumb.settings' }
  },

  /* -------------------- HOME -------------------- */
  {
    path: '',
    component: HomePageComponent,
    data: { breadcrumb: 'breadcrumb.home' }
  }
];
